import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { usePolling } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import {
  WorkflowStatus,
  useBookingProfile_GetCreateBookingKitWfStatusesLazyQuery
} from "@graphql";
import PulsingDot from "@common/pulsing-dot";
import { lightBlue } from "@material-ui/core/colors";
import { useNavManager } from "@hooks";
import { KIT_SLIDER } from "@navigation/locations";
import { KitProfileLocation } from "@navigation/locations";

export type Props = {
  tabActive: boolean;
  bookingId: string;
  handleRunAutoCreate: () => void;
  autoCreateBookingKitDisabled: boolean;
};

type NormalizedStatus = "running" | "failed" | "completed" | "pending";

const getNormalizedStatus = (
  status: WorkflowStatus,
  resultStatus?: string
): NormalizedStatus => {
  if (status === WorkflowStatus.Running) return "running";
  if (status === WorkflowStatus.Failed) return "failed";
  if (status === WorkflowStatus.Completed) {
    if (resultStatus === "failed") return "failed";
    if (resultStatus === "completed") return "completed";
    return "completed";
  }
  return "pending";
};

const getStatusIcon = (normalized: NormalizedStatus) => {
  if (normalized === "running") return <PulsingDot variant="info" />;
  if (normalized === "failed") return <ErrorIcon color={"error"} />;
  if (normalized === "completed") return <CheckCircleIcon color={"secondary"} />;
  return <ScheduleIcon />;
};

const getCardColor = (normalized: NormalizedStatus) => {
  if (normalized === "running") return "info";
  if (normalized === "failed") return "error";
  if (normalized === "completed") return "success";
  return "default";
};

const getStatusText = (normalized: NormalizedStatus) => {
  if (normalized === "running")
    return locale["Processing pending"] || "Processing pending";
  if (normalized === "failed")
    return locale["Kit creation failed!"] || "Kit creation failed!";
  if (normalized === "completed") return locale["Kit created!"] || "Kit created!";
  return locale["Scheduled"] || "Scheduled";
};

const formatSwedishDate = (date?: string | number | Date) => {
  if (!date) return null;
  try {
    return format(new Date(date), "d MMM yyyy, HH:mm", { locale: sv });
  } catch {
    return null;
  }
};

export const BookingKitWfStatusesHistory = ({
  tabActive,
  bookingId,
  handleRunAutoCreate,
  autoCreateBookingKitDisabled
}: Props) => {
  const classes = useStyles();
  const nav = useNavManager();

  const [fetchWorkflowHistory, { data, loading, error }] =
    useBookingProfile_GetCreateBookingKitWfStatusesLazyQuery();

  // Poll when tab is active
  usePolling(
    () => {
      if (tabActive) {
        fetchWorkflowHistory({
          variables: { bookingId, workflowType: "createBookingKit" },
          fetchPolicy: "network-only"
        });
      }
    },
    "*",
    { interval: 5000, startImmediate: tabActive, refetchOnPushState: true }
  );

  if (!tabActive) {
    return null;
  }

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {locale["Error loading workflow history"] || "Error loading workflow history"}
      </Alert>
    );
  }

  const workflowRuns = data?.getWorkflowRuns || [];

  if (workflowRuns.length === 0) {
    return (
      <>
        <Alert severity="info">
          {locale["No workflow history found"] || "No workflow history found"}
        </Alert>
      </>
    );
  }

  return (
    <Box className={classes.container}>
      <div className={classes.headerRow}>
        <div className={classes.titleGroup}>
          <Typography variant="h6" gutterBottom={false}>
            {locale["Kit Automation History"] || "Kit Automation History"}
          </Typography>
        </div>
        <Button
          disabled={autoCreateBookingKitDisabled}
          variant={"contained"}
          color={"primary"}
          size="small"
          onClick={() => handleRunAutoCreate()}
          className={classes.autoButton}
        >
          AUTO
        </Button>
      </div>

      {workflowRuns.map((run, index: number) => {
        const resultStatus = run.result?.data?.status;
        const normalized = getNormalizedStatus(run.status, resultStatus);
        const statusIcon = getStatusIcon(normalized);
        const statusText = getStatusText(normalized);
        const cardColor = getCardColor(normalized);

        // Pick which date to show based on status
        const dateToShow =
          normalized === "running"
            ? run.startedAt || run.scheduledAt
            : run.completedAt || run.startedAt;

        const formattedDate = formatSwedishDate(dateToShow);
        const reason =
          run.result?.data?.message || run.result?.data?.status || run.status;

        return (
          <Card
            key={index}
            className={`${classes.card} ${
              classes[`card${cardColor.charAt(0).toUpperCase() + cardColor.slice(1)}`]
            }`}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                  <Box className={classes.iconWrapper}>{statusIcon}</Box>
                  <Box>
                    <Typography variant="subtitle1" className={classes.statusText}>
                      {statusText}
                    </Typography>
                    {formattedDate && (
                      <Typography variant="body2" color="textSecondary">
                        {locale["Last updated"] || "Senast uppdaterad"}: {formattedDate}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {run.result?.data?.kitId && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      nav.navManager.open(
                        KIT_SLIDER,
                        new KitProfileLocation(run.result.data.kitId)
                      )
                    }
                    className={classes.kitButton}
                  >
                    {locale["View Kit"] || "View Kit"}
                  </Button>
                )}
              </Box>

              {normalized !== "running" &&
                normalized !== "completed" &&
                reason &&
                reason !== statusText && (
                  <Box mt={2}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>{locale["Reason"] || "Reason"}:</strong> {reason}
                    </Typography>
                  </Box>
                )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2)
  },
  card: {
    marginBottom: theme.spacing(2),
    "&:last-child": {
      marginBottom: 0
    }
  },
  cardSuccess: {
    borderLeft: `4px solid ${theme.palette.success.main}`,
    background: alpha(theme.palette.success.main, 0.1)
  },
  cardError: {
    borderLeft: `4px solid ${theme.palette.error.main}`,
    background: alpha(theme.palette.error.main, 0.1)
  },
  cardInfo: {
    borderLeft: `4px solid ${lightBlue[500]}`,
    background: alpha(lightBlue[500], 0.1)
  },
  cardDefault: {
    borderLeft: `4px solid ${theme.palette.grey[400]}`,
    background: alpha(theme.palette.grey[400], 0.1)
  },
  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32
  },
  statusText: {
    fontWeight: "bold"
  },
  accordion: {
    boxShadow: "none",
    "&:before": {
      display: "none"
    },
    "&.Mui-expanded": {
      margin: 0
    }
  },
  accordionSummary: {
    minHeight: "auto",
    padding: 0,
    "& .MuiAccordionSummary-content": {
      margin: 0
    }
  },
  accordionDetails: {
    padding: theme.spacing(1, 0, 0, 0)
  },
  stepsList: {
    width: "100%"
  },
  stepItem: {
    paddingLeft: 0,
    paddingRight: 0
  },
  stepIcon: {
    minWidth: 32
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    margin: theme.spacing(2, 0)
  },
  titleGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5)
  },
  autoButton: {
    minWidth: "auto",
    padding: theme.spacing(0.5, 1.5),
    fontSize: "0.875rem",
    fontWeight: "bold",
    textTransform: "uppercase",
    borderRadius: theme.spacing(0.5)
  },
  kitButton: {
    minWidth: "auto",
    padding: theme.spacing(0.5, 1.5),
    fontSize: "0.875rem",
    borderRadius: theme.spacing(0.5)
  }
}));
